import { useMemo, useState } from "react";
import { toast } from "@/state/hooks";
import { Loader2, UserPlus } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/ui/components/ui/dialog";
import { Input } from "@/ui/components/ui/input";
import { Button } from "@/ui/components/ui/button";
import { ScrollArea } from "@/ui/components/ui/scroll-area";
import { RadioGroup, RadioGroupItem } from "@/ui/components/ui/radio-group";
import { Label } from "@/ui/components/ui/label";
import { useActions, useAppState } from "@/state/legacy_module_api";
import { memberSchema, type Contact } from "@/data/schemas";

interface GroupMemberAddPopupProps {
  open: boolean;
  onClose: () => void;
  groupId?: string;
}

const GroupMemberAddPopup = ({
  open,
  onClose,
  groupId,
}: GroupMemberAddPopupProps) => {
  const { usersList } = useAppState().user;
  const { threadMap } = useAppState().group;
  const { addGroupMember } = useActions().group;

  const group = groupId ? threadMap[groupId] : undefined;
  const existingMemberIds = useMemo(
    () => new Set(group?.members?.map((m) => m.userId) || []),
    [group]
  );

  const availableUsers = useMemo(
    () => usersList.filter((u: Contact) => !existingMemberIds.has(u.userId)),
    [existingMemberIds, usersList]
  );

  const [searchText, setSearchText] = useState("");
  const [selectedUserId, setSelectedUserId] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const filteredUsers = useMemo(() => {
    const query = searchText.toLowerCase();
    if (!query) return availableUsers;
    return availableUsers.filter(
      (user) =>
        user.username?.toLowerCase().includes(query) ||
        user.userId.toLowerCase().includes(query)
    );
  }, [availableUsers, searchText]);

  const handleSubmit = async () => {
    if (!group) {
      toast.error("Group not found.");
      return;
    }
    if (!selectedUserId) {
      toast.error("Please select a user");
      return;
    }
    if (group.members?.length && group.members.length >= 50) {
      toast.error("This group already has the maximum number of members (50).");
      return;
    }

    setIsLoading(true);
    try {
      const user = usersList.find((u: Contact) => u.userId === selectedUserId);
      const member = memberSchema.parse({
        userId: selectedUserId,
        role: "member",
        joinedAt: Date.now(),
      });
      await addGroupMember({ member, group });
      toast.success("Member added successfully");
      setSelectedUserId("");
      setSearchText("");
      onClose();
    } catch (error: any) {
      console.error("Error adding member:", error);
      toast.error(error.message || "An error occurred while adding the member");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserPlus className="w-5 h-5 text-muted-foreground" />
            Add Member
          </DialogTitle>
          <DialogDescription>
            {group
              ? `Select a contact to add to ${group.name}.`
              : "Select a group to add members."}
          </DialogDescription>
        </DialogHeader>

        {!group ? (
          <p className="text-sm text-muted-foreground">Group not found.</p>
        ) : availableUsers.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            No more contacts available to add.
          </p>
        ) : (
          <>
            <Input
              placeholder="Search by name or ID"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
            />

            <RadioGroup value={selectedUserId} onValueChange={setSelectedUserId}>
              <ScrollArea className="max-h-[55vh] pr-2">
                <div className="space-y-2">
                  {filteredUsers.map((user) => (
                    <Label
                      key={user.userId}
                      htmlFor={user.userId}
                      className="flex cursor-pointer items-center gap-3 rounded-lg border bg-muted/30 px-3 py-2"
                    >
                      <RadioGroupItem value={user.userId} id={user.userId} />
                      <div className="flex-1">
                        <p className="font-medium">{user.username}</p>
                        <p className="text-xs text-muted-foreground">
                          {user.userId}
                        </p>
                      </div>
                    </Label>
                  ))}
                  {filteredUsers.length === 0 && (
                    <p className="text-sm text-muted-foreground px-1">
                      No contacts match that search.
                    </p>
                  )}
                </div>
              </ScrollArea>
            </RadioGroup>

            <Button
              onClick={handleSubmit}
              disabled={isLoading || !selectedUserId}
              className="w-full"
            >
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Adding...
                </>
              ) : (
                "Add Member"
              )}
            </Button>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default GroupMemberAddPopup;
